"use client";
import {
  CreditCard,
  CustomButton,
  FlexBetween,
  FlexBetweenCol,
  Logo,
} from "@/components";
import { useCardsInquiryMutation } from "@/hooks/useInquiry";
import {
  IServiceFormProps,
  TCreditCard,
  TRawServiceApiKeys,
} from "@/interfaces";
import { cn } from "@/lib";
import { useAppStore, useModalStore, useSecureStore } from "@/store";
import { creditCardSchema } from "@/validations";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSession } from "next-auth/react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";
import InquiryCost from "../../inquiryCost";
import { useTranslations } from "next-intl";
import { routes } from "@/routes";
import { servicesQuickAccessNavigation } from "@/constants";
import { globalRawServicesData } from "@/constants/globalRawServicesData/globalRawServicesData";
import React, { useEffect, useState } from "react";
import HtmlRenderer from "../../htmlRenderer";
import BankCreditCard from "@/components/inquiries/bankCreditCard";
import MainLogo from "@public/svg/logo-2.svg";
import Loader from "@/components/ui/loader";
import PaymentModalContainer from "@/components/Payment/paymentModalContainer";
import { ICardsInquiryMutationPayload } from "@/interfaces/inquiries/bank";
import SampleInquiryModal from "@/components/inquirySamples/sampleInquiryModal";
import CardInquirySample from "@/components/inquirySamples/cardInquirySample";

const CardInquiryForm = ({
  cost,
  isLoggedIn,
  navigateToLogin,
  announcement,
  disableService,
  bankKey,
  serviceId,
}: IServiceFormProps) => {
  const { data: session } = useSession();
  const { persistedFields } = useSecureStore();
  const {
    isPending: isSubmitting,
    mutate: cardsInquiryFn,
    error: cardsInquiryError,
    reset: resetCardsInquiry,
  } = useCardsInquiryMutation(session?.user.token);

  const router = useRouter();
  const { openModal, closeModal, setOpenModal } = useModalStore();
  const isPaymentMethodModalOpen = openModal === "payment-method";
  const openPaymentModal = () => {
    setOpenModal("payment-method");
  };

  const serviceKey = TRawServiceApiKeys.Card_Inquiry;

  const {
    control,
    handleSubmit,
    formState: { errors: formError },
    getValues,
    setValue,
  } = useForm<TCreditCard>({
    resolver: zodResolver(creditCardSchema),
    defaultValues: {
      input1: persistedFields?.[serviceKey]?.input1 || "",
      input2: persistedFields?.[serviceKey]?.input2 || "",
      input3: persistedFields?.[serviceKey]?.input3 || "",
      input4: persistedFields?.[serviceKey]?.input4 || "",
    },
  });

  const t = useTranslations("serviceFormSubmissions");
  const [error, setError] = useState<string | null>(null);
  const pathname = usePathname();
  const { setPostLoginAction } = useAppStore();
  const activeKey = servicesQuickAccessNavigation.find(
    (item) => item.key === globalRawServicesData.Card_Inquiry.key,
  )!;
  const [packageId, setPackageId] = useState<string | null>(null);
  const [isSampleOpen, setIsSampleOpen] = useState(false);
  // Handle package selection
  const handlePackageSelect = (packageId: string) => {
    setPackageId(packageId);
  };
  const searchParams = useSearchParams();
  useEffect(() => {
    if (searchParams?.get("payment") === "1") {
      setOpenModal("payment-method");
    }
  }, [searchParams, setOpenModal]);

  useEffect(() => {
    if (isPaymentMethodModalOpen) {
      resetCardsInquiry();
    }
  }, [isPaymentMethodModalOpen]);

  const onSubmit = (data: any) => {
    const fullNumber = Object.values(data).join("");
    if (!isLoggedIn) {
      setPostLoginAction("open-payment-modal");
      navigateToLogin();
      return;
    }
    setError(null);
    openPaymentModal();
  };

  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timerEndTime, setTimerEndTime] = useState<number | null>(null);

  const paymentSubmission = () => {
    const formData = getValues();
    const fullCard = Object.values(formData).join("");
    const payload: Readonly<ICardsInquiryMutationPayload> = {
      number: fullCard,
      campaign: packageId || undefined,
    };
    cardsInquiryFn(
      { payload },
      {
        onSuccess: (data) => {
          const purchase_id = data.data?.purchase_id;
          if (purchase_id) {
            router.push(
              `${routes.main.INQUIRY_DETAILS.primaryPath}/${data.data?.purchase_id}`,
            );
            closeModal();
          }
          setError(null);
          setIsTimerActive(false);
          setTimerEndTime(null);
        },
        onError: (err) => {
          let message = "خطای نا‌مشخص از سرور";
          if (err instanceof Error) {
            message = err.message || message;
          }
          setError(message);
          setIsTimerActive(true); // Start timer
          setTimerEndTime(Date.now() + 60000);
        },
      },
    );
  };
  const handleTimerComplete = () => {
    setIsTimerActive(false);
    setTimerEndTime(null);
  };
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FlexBetweenCol gap={8} className={cn("w-full")}>
          <div>
            {bankKey ? (
              <BankCreditCard
                bankKey={bankKey}
                variant="credit"
                formError={formError}
                control={control}
                setValue={setValue}
              />
            ) : (
              <CreditCard
                variant="credit"
                formError={formError}
                control={control}
                setValue={setValue}
              />
            )}

            {Object.keys(formError).length > 0 && (
              <p className="text-error-main mt-4 w-full text-xs">
                {t("cardInquiry.cardError")}
              </p>
            )}
          </div>
          {session?.user.token && <InquiryCost cost={cost} />}
          {announcement && <HtmlRenderer htmlString={announcement} />}
          <FlexBetween gap={2} className="lg:gap-4">
            {/*<CustomButton*/}
            {/*  id="card-inquiry-sample"*/}
            {/*  type="button"*/}
            {/*  className="border-primary-main text-primary-main hover:bg-primary-main/5 border bg-transparent !px-3 !text-sm"*/}
            {/*  onClick={() => setIsSampleOpen(true)}*/}
            {/*>*/}
            {/*  نمونه نتیجه*/}
            {/*</CustomButton>*/}
            <CustomButton
              id="card-inquiry-submit"
              disabled={disableService}
              className="flex-1"
              variant="primary"
              type="submit"
            >
              {t("cardInquiry.confirmButton")}
            </CustomButton>
          </FlexBetween>
        </FlexBetweenCol>
      </form>
      {isPaymentMethodModalOpen && (
        <PaymentModalContainer
          payBtnId="payment-card-inquiry"
          submissionError={cardsInquiryError}
          submissionLoading={isSubmitting}
          paymentSubmission={paymentSubmission}
          isOpen={isPaymentMethodModalOpen}
          onClose={closeModal}
          selectedService={activeKey}
          onErrorReset={resetCardsInquiry}
          onPackageSelect={handlePackageSelect}
          serviceId={serviceId}
          showData="service"
          persistedData={{
            [serviceKey]: {
              ...getValues(),
            },
          }}
          showManualError={error}
          isTimerActive={isTimerActive}
          timerEndTime={timerEndTime}
          onTimerComplete={handleTimerComplete}
        />
      )}
      {isSampleOpen && (
        <SampleInquiryModal
          onClose={() => setIsSampleOpen(false)}
          title="نمونه نتیجه استعلام کارت بانکی"
        >
          <CardInquirySample />
        </SampleInquiryModal>
      )}
      {isSubmitting && (
        <div className="flex-center fixed inset-0 z-[1000] flex bg-gray-900/60">
          <div className="flex-column flex-center flex w-60 rounded-xl bg-white p-3 sm:w-80 sm:p-4">
            <Logo className="mb-3" src={MainLogo} width={60} height={60} />
            <h2 className="text-center text-sm font-semibold sm:text-base">
              درحال استعلام <br /> لطفا شکیبا باشید
            </h2>
            <div className="mt-4 size-4 sm:size-5">
              <Loader color="bg-primary-main" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CardInquiryForm;
